import apiClient from './apiClient';
import { MissionHistoryResponse, MissionHistoryRequest } from '../types/missionHistory';

class MissionHistoryService {
  private baseUrl = '/api/missions';

  // Получить историю Airsoft миссий пользователя
  async getUserAirsoftMissionHistory(request: MissionHistoryRequest): Promise<MissionHistoryResponse> {
    try {
      const params = new URLSearchParams();
      
      if (request.page) params.append('page', request.page.toString());
      if (request.pageSize) params.append('pageSize', request.pageSize.toString());
      if (request.dateFrom) params.append('dateFrom', request.dateFrom);
      if (request.dateTo) params.append('dateTo', request.dateTo);
      if (request.specificDate) params.append('specificDate', request.specificDate);
      if (request.result) params.append('result', request.result);
      if (request.fieldId) params.append('fieldId', request.fieldId);
      if (request.refereeId) params.append('refereeId', request.refereeId);
      if (request.missionName) params.append('missionName', request.missionName);
      if (request.faction) params.append('faction', request.faction);

      const url = `${this.baseUrl}/airsoft/user/${request.userId}?${params.toString()}`;
      console.log('Getting airsoft mission history:', url);
      
      const response = await apiClient.get<MissionHistoryResponse>(url);
      console.log('Airsoft mission history response:', response);
      
      return response;
    } catch (error) {
      console.error('Error getting airsoft mission history:', error);
      throw error;
    }
  }

  // Получить историю Paintball миссий пользователя
  async getUserPaintballMissionHistory(request: MissionHistoryRequest): Promise<MissionHistoryResponse> {
    try {
      const params = new URLSearchParams();
      
      if (request.page) params.append('page', request.page.toString());
      if (request.pageSize) params.append('pageSize', request.pageSize.toString());
      if (request.dateFrom) params.append('dateFrom', request.dateFrom);
      if (request.dateTo) params.append('dateTo', request.dateTo);
      if (request.specificDate) params.append('specificDate', request.specificDate);
      if (request.result) params.append('result', request.result);
      if (request.fieldId) params.append('fieldId', request.fieldId);
      if (request.refereeId) params.append('refereeId', request.refereeId);
      if (request.missionName) params.append('missionName', request.missionName);
      if (request.faction) params.append('faction', request.faction);

      const url = `${this.baseUrl}/paintball/user/${request.userId}?${params.toString()}`;
      console.log('Getting paintball mission history:', url);
      
      const response = await apiClient.get<MissionHistoryResponse>(url);
      console.log('Paintball mission history response:', response);
      
      return response;
    } catch (error) {
      console.error('Error getting paintball mission history:', error);
      throw error;
    }
  }

  // Форматировать дату для API
  formatDateForApi(date: Date): string {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  // Форматировать дату и время для отображения
  // Дата показывается как есть (локальное время поля), без конвертации UTC -> локальное время браузера
  formatDateTime(dateString: string): string {
    // Парсим ISO строку без конвертации в локальное время
    // "2025-12-06T12:35:00Z" -> показываем "Dec 6, 2025, 12:35 PM" (время поля)
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) {
      // Fallback для нестандартных форматов
      return dateString;
    }

    const [, year, month, day, hours, minutes] = match;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month, 10) - 1];

    // Конвертируем 24h в 12h формат
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 < 12 ? 'AM' : 'PM';

    return `${monthName} ${parseInt(day, 10)}, ${year}, ${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  }

  // Получить отображаемое название результата
  getResultDisplayName(result: string): string {
    switch (result.toLowerCase()) {
      case 'victory':
        return 'Victory';
      case 'defeat':
        return 'Defeat';
      case 'draw':
        return 'Draw';
      default:
        return result;
    }
  }

  // Получить цвет для результата
  getResultColor(result: string): string {
    switch (result.toLowerCase()) {
      case 'victory':
        return '#4CAF50'; // Зеленый
      case 'defeat':
        return '#F44336'; // Красный
      case 'draw':
        return '#FF9800'; // Оранжевый
      default:
        return '#9E9E9E'; // Серый
    }
  }

  // Получить отображаемое название фракции
  getFactionDisplayName(faction: string): string {
    switch (faction.toLowerCase()) {
      case 'human':
        return 'HUMAN';
      case 'alien':
        return 'ALIEN';
      default:
        return faction.toUpperCase();
    }
  }

  // Получить цвет для фракции
  getFactionColor(faction: string): string {
    switch (faction.toLowerCase()) {
      case 'human':
        return '#3b82f6'; // Синий
      case 'alien':
        return '#ff8800'; // Оранжевый
      default:
        return '#9E9E9E'; // Серый
    }
  }
}

export default new MissionHistoryService();